import { useEffect, useState } from 'react'
import { Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { DemographicFunctions } from './DemographicFunctions'

import { ContainerGroupField } from '@/components/common/ContainerGroupField'
import { AutocompleteControl } from '@/components/form-controls'
import { DemographicFormData, OptionsField } from '@/types/forms/'

interface BornControlProps {
    control: Control<DemographicFormData>
    watchCountryBorn: string
    watchDepartmentBorn: string
    text: string
    setBornCities: (cities: OptionsField[]) => void
    onFieldChange?: (field: string, value: any) => void
    setValue?: (field: keyof DemographicFormData, value: any) => void
    nationalityOptions?: OptionsField[]
    trigger?: (name: keyof DemographicFormData) => Promise<boolean>
}

// eslint-disable-next-line max-lines-per-function
export const BornControl = ({
    control,
    watchCountryBorn,
    watchDepartmentBorn,
    text,
    setBornCities,
    onFieldChange,
    setValue,
    trigger,
    nationalityOptions
}: BornControlProps): JSX.Element => {
    const { t } = useTranslation()

    const [bornCountries, setBornCountriesLocal] = useState<OptionsField[]>([])
    const [bornDepartments, setBornDepartmentsLocal] = useState<OptionsField[]>(
        []
    )
    const [nationalityList, setNationalityListLocal] = useState<OptionsField[]>(
        nationalityOptions && nationalityOptions.length > 0
            ? nationalityOptions
            : []
    )
    const [bornCities, setBornCitiesLocal] = useState<OptionsField[]>([])

    const {
        fetchCountries,
        fetchDepartments,
        fetchCities,
        fetchNationalitiesList
    } = DemographicFunctions()

    //* Country
    useEffect(() => {
        let mounted = true

        const fetchBornCountries = async (): Promise<void> => {
            const response = await fetchCountries(mounted)

            if (mounted && response) {
                setBornCountriesLocal(response)
            }
        }

        fetchBornCountries()

        return (): void => {
            mounted = false
        }
    }, [])

    //* Nationality - use provided options or fetch once if absent
    useEffect(() => {
        let mounted = true
        const shouldFetch =
            !nationalityOptions || nationalityOptions.length === 0

        const filterEmptyOptions = (options: OptionsField[]) =>
            options.filter(
                (opt) =>
                    opt.value &&
                    opt.label &&
                    String(opt.value).trim() !== '' &&
                    String(opt.label).trim() !== '' &&
                    String(opt.label).toLowerCase() !== 'unknown' &&
                    String(opt.label).trim() !== '.'
            )

        if (!shouldFetch) {
            setNationalityListLocal(filterEmptyOptions(nationalityOptions!))
        } else {
            const fetchNationalityList = async (): Promise<void> => {
                const response = await fetchNationalitiesList(mounted)

                if (mounted && response) {
                    setNationalityListLocal(filterEmptyOptions(response))
                }
            }

            fetchNationalityList()
        }

        return (): void => {
            mounted = false
        }
    }, [nationalityOptions])

    // Auto-select and lock nationality when countryBorn is CO
    useEffect(() => {
        if (
            watchCountryBorn === 'CO' &&
            nationalityList.length > 0 &&
            setValue
        ) {
            const colombian =
                nationalityList.find(
                    (opt) =>
                        opt.value?.toString().toUpperCase() === 'CO' ||
                        opt.label?.toString().toUpperCase().includes('COLOMBI')
                ) ||
                nationalityList.find((opt) =>
                    opt.label?.toUpperCase().includes('COLOMB')
                )

            if (colombian) {
                setValue('nationality', colombian.value)
                if (trigger) {
                    trigger('nationality')
                }
            }
        }
    }, [watchCountryBorn, nationalityList, setValue, trigger])

    //* Department
    useEffect(() => {
        let mounted = true

        // Si no hay país seleccionado, limpiar departamentos
        if (!watchCountryBorn || watchCountryBorn === '') {
            if (mounted) {
                setBornDepartmentsLocal([])
            }

            return
        }

        const fetchBornDepartments = async (): Promise<void> => {
            const response = await fetchDepartments(mounted, watchCountryBorn)

            if (mounted && response) {
                setBornDepartmentsLocal(response)
            }
        }

        fetchBornDepartments()

        return (): void => {
            mounted = false
        }
    }, [watchCountryBorn])

    //* City
    useEffect(() => {
        let mounted = true

        // Si no hay país o departamento seleccionado, limpiar ciudades
        if (
            !watchCountryBorn ||
            watchCountryBorn === '' ||
            !watchDepartmentBorn ||
            watchDepartmentBorn === ''
        ) {
            if (mounted) {
                setBornCities([])
                setBornCitiesLocal([])
            }

            return
        }

        const fetchBornCities = async (): Promise<void> => {
            const response = await fetchCities(
                mounted,
                watchCountryBorn,
                watchDepartmentBorn
            )

            if (mounted && response) {
                setBornCities(response)
                setBornCitiesLocal(response)
            }
        }

        fetchBornCities()

        return (): void => {
            mounted = false
        }
    }, [watchCountryBorn, watchDepartmentBorn])

    return (
        <ContainerGroupField tittleGroup={t('forms.groups.born')}>
            <AutocompleteControl
                name="countryBorn"
                control={control}
                label={t(`${text}.countryBorn`)}
                options={bornCountries}
                rules={{
                    required: t(`${text}.required.countryBorn`)
                }}
                onChangeCustom={(value) =>
                    onFieldChange?.('countryBorn', value)
                }
            />
            <AutocompleteControl
                name="departmentBorn"
                control={control}
                label={t(`${text}.departmentBorn`)}
                options={bornDepartments}
                placeholder={
                    !watchCountryBorn
                        ? t('forms.common.placeholders.selectCountryFirst')
                        : t(`${text}.departmentBorn`)
                }
                isDisabled={!watchCountryBorn}
                rules={{
                    required: t(`${text}.required.departmentBorn`)
                }}
                onChangeCustom={(value) =>
                    onFieldChange?.('departmentBorn', value)
                }
            />
            {watchCountryBorn === 'CO' && (
                <AutocompleteControl
                    name="cityBorn"
                    control={control}
                    label={t(`${text}.cityBorn`)}
                    options={bornCities}
                    placeholder={
                        !watchDepartmentBorn
                            ? t('forms.common.placeholders.selectDepartmentFirst')
                            : t(`${text}.cityBorn`)
                    }
                    isDisabled={!watchDepartmentBorn}
                    rules={{
                        required: t(`${text}.required.cityBorn`)
                    }}
                />
            )}
            <AutocompleteControl
                name="nationality"
                control={control}
                label={t(`${text}.nationality`)}
                options={
                    nationalityList.length > 0
                        ? nationalityList
                        : [
                            {
                                label: t('forms.ui.loading'),
                                value: '__loading__'
                            }
                        ]
                }
                rules={{
                    required: t(`${text}.required.nationality`)
                }}
                isDisabled={watchCountryBorn === 'CO'}
                // Asegurar que el campo siempre sea visible
                className="nationality-field"
            />
        </ContainerGroupField>
    )
}
